import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Palette, Search, Shield, TrendingUp, Headphones } from 'lucide-react';

const reasons = [
  {
    icon: Zap,
    title: 'Lightning Speed',
    description: 'Optimized performance with Core Web Vitals scores that boost your SEO rankings.',
  },
  {
    icon: Palette,
    title: 'Premium Design',
    description: 'Modern, aesthetic interfaces crafted with attention to every pixel and interaction.',
  },
  {
    icon: Search,
    title: 'SEO-First',
    description: 'Built from the ground up with search engine visibility and organic growth in mind.',
  },
  {
    icon: Shield,
    title: 'Rock-Solid Security',
    description: 'Enterprise-grade security practices to protect your business and customer data.',
  },
  {
    icon: TrendingUp,
    title: 'Scalability',
    description: 'Architecture that grows with your business, handling traffic spikes effortlessly.',
  },
  {
    icon: Headphones,
    title: '24/7 Support',
    description: 'Dedicated support team ready to help you succeed, whenever you need us.',
  },
];

const WhyChooseUs = () => {
  return (
    <section 
      id="why-us" 
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #050508 0%, #08080f 50%, #050508 100%)',
      }}
      data-testid="why-us-section"
    >
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-[#7B2EFF]/5 rounded-full blur-[180px]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Floating Glass Panel with Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Main glass panel */}
            <div className="relative glass-panel rounded-3xl p-2 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1485740112426-0c2549fa8c86?w=800&q=80"
                alt="Futuristic Technology"
                className="w-full h-[300px] sm:h-[350px] md:h-[450px] object-cover rounded-2xl"
              />
              
              {/* Overlay stats */}
              <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 right-2 sm:right-4 grid grid-cols-2 gap-2 sm:gap-3">
                <motion.div 
                  className="glass-card rounded-xl sm:rounded-2xl p-2 sm:p-3 md:p-4"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white">100%</div>
                  <div className="text-xs sm:text-sm text-white/50">On-Time Delivery</div>
                </motion.div>
                <motion.div 
                  className="glass-card rounded-xl sm:rounded-2xl p-2 sm:p-3 md:p-4"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#7B2EFF] to-[#00D4FF]">A+</div>
                  <div className="text-xs sm:text-sm text-white/50">Performance Score</div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Right - Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5 }}
              className="mb-10"
            >
              <span className="inline-block px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] text-[#7B2EFF] bg-[#7B2EFF]/10 border border-[#7B2EFF]/20 backdrop-blur-sm mb-4">
                Why Choose Us
              </span>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold text-white">Built Different, Built Better</h2>
              <p className="mt-4 text-white/50 text-lg">
                We don't just build websites. We craft digital experiences that drive results.
              </p>
            </motion.div>

            {/* Reasons Grid - Glass Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {reasons.map((reason, index) => (
                <motion.div
                  key={reason.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group flex gap-4 items-start p-4 rounded-2xl glass-card"
                  data-testid={`why-us-card-${index}`}
                >
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl grid place-items-center bg-[#7B2EFF]/10 border border-[#7B2EFF]/30 group-hover:bg-[#7B2EFF]/20 group-hover:shadow-[0_0_20px_rgba(123,46,255,0.3)] transition-all">
                    <reason.icon className="w-5 h-5 text-[#7B2EFF]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{reason.title}</h3>
                    <p className="text-white/40 text-sm leading-relaxed">{reason.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
